import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import {
    buildInterforumS3UploadHeaders,
    readEdiHeaderIdentifier,
} from "components/EDIInterforumSettings/interforumEdiUploadUtils";
import type { TPendingImport } from "containers/BulkUpdatesContainer/ConfirmationStep";
import { downloadFile, translationsWithFallback } from "helpers/utils";
import { t } from "i18next";
import { toast } from "react-toastify";
import { setPendingImports } from "store/dataImportSlice";
import { fetchActiveJobs } from "store/jobsStore";
import type { RootState } from "store/store";
import { ImportTargetsEnum } from "types";

import { EFetchStatus } from "../types";
import type { AutomatedImportState } from "./types";

const failInterforumProcessingJob = async ({
    processingJobId,
    companyId,
    reason,
}: {
    processingJobId: number;
    companyId: number;
    reason: string;
}) => {
    await axios.post(`${import.meta.env.REACT_APP_API_ENDPOINT}/automated-imports/complete-interforum-processing-job`, {
        processingJobId,
        companyId,
        status: "FAILED",
        reason,
    });
};

const registerPendingInterforumJob = (fileName: string, dispatch: (action: unknown) => void) => {
    const pendingImports: TPendingImport[] = JSON.parse(sessionStorage.getItem("pending_imports") ?? "[]");
    const newPendingImports: TPendingImport[] = [
        ...pendingImports,
        {
            filename: fileName,
            date: new Date(),
            importType: ImportTargetsEnum.processInterforumEdi,
        },
    ];

    sessionStorage.setItem("pending_imports", JSON.stringify(newPendingImports));
    dispatch(setPendingImports(newPendingImports));

    const jobsLength = +(sessionStorage.getItem("jobs_length") ?? 0);
    sessionStorage.setItem("jobs_length", String(jobsLength + 1));
};

/**
 * Manually trigger the interforum files sftp files fetching
 * fetch files -> store to s3 -> parse with lambda -> handle bulk import
 */
export const fetchInterforumFiles = createAsyncThunk(
    "automatedImports/getInterforumFiles",
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT + "/automated-imports/handle-interforum-files",
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

/**
 * List all processed EDI files
 */
export const fetchAllProcessedEdiFiles = createAsyncThunk(
    "automatedImports/getAllProcessedEdiFiles",
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT + "/automated-imports/processed-s3-file",
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

/**
 * Upload an Interforum EDI file to sftp-interforum bucket via presigned URL.
 * S3 trigger starts connect-interforum lambda for parsing and archiving.
 */
export const uploadInterforumEdiFile = createAsyncThunk(
    "automatedImports/uploadInterforumEdiFile",
    async (file: File, { rejectWithValue, dispatch, getState }) => {
        let processingJobId: number | undefined;
        let companyId: number | undefined;

        try {
            const ediIdentifier = await readEdiHeaderIdentifier(file);
            const { companySettings, userData } = (getState() as RootState).settings;
            companyId = companySettings?.id;
            const accountId = userData?.id;

            const response = await axios.post(
                `${import.meta.env.REACT_APP_API_ENDPOINT}/automated-imports/interforum-upload-url`,
                { fileName: file.name, ediIdentifier },
            );

            const uploadPayload = response.data.data as {
                signedUrl: string;
                s3Key: string;
                processingJobId: number;
            };

            processingJobId = uploadPayload.processingJobId;
            const { signedUrl } = uploadPayload;

            const uploadResponse = await fetch(signedUrl, {
                method: "PUT",
                body: file,
                headers: buildInterforumS3UploadHeaders(signedUrl, {
                    companyId,
                    processingJobId,
                    accountId,
                }),
            });

            if (!uploadResponse.ok) {
                if (processingJobId && companyId) {
                    await failInterforumProcessingJob({
                        processingJobId,
                        companyId,
                        reason: `S3 upload failed with status ${uploadResponse.status}`,
                    });
                }
                throw new Error(`S3 upload failed with status ${uploadResponse.status}`);
            }

            registerPendingInterforumJob(file.name, dispatch);
            await dispatch(fetchActiveJobs());
            await dispatch(fetchAllProcessedEdiFiles());

            return file.name;
        } catch (error: any) {
            if (processingJobId && companyId && !error?.message?.startsWith("S3 upload failed")) {
                try {
                    await failInterforumProcessingJob({
                        processingJobId,
                        companyId,
                        reason: error?.message ?? "Upload failed",
                    });
                } catch {
                    // Best-effort cleanup; original error is surfaced to the user
                }
            }

            return rejectWithValue(error?.response?.data ?? { message: "InternalServerError" });
        }
    },
);

/**
 * Download an EDI file (GET streaming)
 */
export const downloadEDIFile = createAsyncThunk(
    "automatedImports/downloadEDIFile",
    async (
        {
            s3Key,
            suggestedFileName,
            extension,
        }: { s3Key: string; suggestedFileName: string; extension: ".edi" | ".csv" },
        { rejectWithValue },
    ) => {
        try {
            const _s3Key = encodeURIComponent(s3Key);
            const _suggestedFileName = encodeURIComponent(suggestedFileName + extension);

            const url = `${
                import.meta.env.REACT_APP_API_ENDPOINT
            }/automated-imports/download-edi-file?s3Key=${_s3Key}&suggestedFileName=${_suggestedFileName}`;

            const response = await toast.promise(
                axios.get(url, {
                    responseType: "blob",
                    headers: { Accept: "*/*" },
                }),
                {
                    pending: `${t("Export.Pending")}`,
                    success: `${t("Export.Successful")}`,
                    error: `${t("Export.Error")}`,
                },
            );

            const blob = new Blob([response.data], { type: "application/octet-stream" });

            downloadFile({ source: blob, fileName: _suggestedFileName, isLink: false });
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

const initialState: AutomatedImportState = {
    ediFiles: [],
    ediFilesStatus: EFetchStatus.IDLE,
    uploadEdiFileStatus: EFetchStatus.IDLE,
};

export const automatedImportsSlice = createSlice({
    name: "automatedImports",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(fetchInterforumFiles.fulfilled, (_state, _action) => {
            toast.success(`${t("AdminPanel.HandleInterforumFilesSuccess")}`);
        });
        builder.addCase(fetchInterforumFiles.rejected, (_state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(fetchAllProcessedEdiFiles.pending, (state, action) => {
            state.ediFilesStatus = EFetchStatus.PENDING;
        });
        builder.addCase(fetchAllProcessedEdiFiles.fulfilled, (state, action) => {
            state.ediFiles = action.payload;
            state.ediFilesStatus = EFetchStatus.FULFILLED;
        });
        builder.addCase(fetchAllProcessedEdiFiles.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.ediFilesStatus = EFetchStatus.REJECTED;
        });
        builder.addCase(uploadInterforumEdiFile.pending, (state) => {
            state.uploadEdiFileStatus = EFetchStatus.PENDING;
        });
        builder.addCase(uploadInterforumEdiFile.fulfilled, (state) => {
            state.uploadEdiFileStatus = EFetchStatus.FULFILLED;
            toast.success(`${t("Settings.EDIInterforum.UploadSuccess")}`);
        });
        builder.addCase(uploadInterforumEdiFile.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "interforum-edi-upload-error" });
            state.uploadEdiFileStatus = EFetchStatus.REJECTED;
        });
    },
});

export default automatedImportsSlice.reducer;
